import {SetTitleBarWithDescription} from "../../core/layout/titleBar/titleBarApi";
import {Localizer} from "../../infrastructure/localization/localizer";
import {KursusoverviewModel} from "./models/KursusoverviewModel";
import ReactAsyncTable from "../../core/components/reactTable/ReactAsyncTable";
import { useKursusOverview } from "./hooks/useKursusOverview";
import { useKursusOverviewColumns } from "./hooks/useKursusOverviewColumns";
import { closeModal, SlideIn } from "core/components/slideIn/slideIn";
import { KursusOverviewFilter } from "./components/kursusOverviewFilter";
import { useMemo, useState } from "react";
import useUserFilterPermissions from "core/hooks/useUserFilterPermissions";
import ApplyFiltrerButton from "core/components/filter/components/applyFiltrerButton";
import { hasAnyDefinedProperties } from "index";
import { RemoveFiltrerButton } from "core/components/filter/components/removeFiltrerButton";
import { AsyncButton } from "core/components/button/asyncButton";
import { EmblaIcon } from "core/components/emblaIcon/emblaIcon";
import { EmblaIcons } from "core/emblaIcons";
import KursusOverviewDisplayAppliedFilters from "./components/kursusOverviewDisplayAppliedFilters";
import DynamicButton from "core/components/button/dynamicButton";
import useUser from "core/hooks/useUser";
import Tooltip from "core/components/tooltips/Tooltip";
import { EmailConstants } from "core/components/mail/emailConstants";
import { EmailSlideInModalMultipleUsers } from "core/components/mail/emailSlideInModalMultipleUsers";
import { UserSendEmailResponseModel } from "services/api/stamdataUser/UserSendEmailResponseModel";
import useLogbogApi from "core/hooks/useLogbogApi";

const filterModalId = "kursusoverview-filter-modal";
const sendEmailButtonId = "send-email-btn";
const modalTarget = "send-email-modal"

export const KursusOverview = () => {
  SetTitleBarWithDescription(Localizer.global_kursusoversigt(), Localizer.kursusoverview_beskrivelse())
  const { 
    data, 
    asyncListInput, 
    setAsyncListInput, 
    setFilter, 
    clearFilter,
    exportExcel,
    paginationModel, 
    isLoading,
    fetchFailed
  } = useKursusOverview();
  
  const { columns } = useKursusOverviewColumns();
  const { permissions } = useUserFilterPermissions();
  const { currentUser } = useUser();
  const {logbogLaegekursusApi} = useLogbogApi();
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [showEmailSlideIn, setShowEmailSlideIn] = useState<boolean>(false)
  const [filteredUserModels, setFilteredUserModels] = useState<UserSendEmailResponseModel[]>([])

  const closeFilterModal = () => {
    closeModal(filterModalId);
    setFilterModalOpen(false);
  };

  const closeEmailModal = () => {
    closeModal();
    setShowEmailSlideIn(false);
  };

  const handleOnSendEmailClick = async () => {
      const filteredUsers = await logbogLaegekursusApi.getKursusOverviewUserEmails(asyncListInput);
      setFilteredUserModels(filteredUsers.data)
      setShowEmailSlideIn(true);
  };

  const filterApplied = useMemo(() => hasAnyDefinedProperties(asyncListInput.kursusoverviewFilter), [asyncListInput.kursusoverviewFilter])
  const shouldShowTooltip = paginationModel?.filteredCount == undefined ? false : paginationModel?.filteredCount > EmailConstants.maxAmount
  const shouldButtonBeDisabled = paginationModel?.filteredCount == undefined ? true : paginationModel?.filteredCount > EmailConstants.maxAmount
    
  return (
    <>
      <ReactAsyncTable
        tableId="kursusoverview"
        title={Localizer.kursusoverview_brugerKursusRaekker()}
        renderContainerHeaderItems={
          <div className="flex-container flex-align-c">
            <AsyncButton 
              clickFunction={exportExcel}
              buttonText={Localizer.GetExcel()}
              waitText={Localizer.GettingExcel()}
              child={<EmblaIcon iconName={EmblaIcons.Incoming}/>}  
            />
            {currentUser.canSendMultipleEmails() &&
                  <Tooltip show={shouldShowTooltip} title={Localizer.validate_emailBulkAllowanceMessage(EmailConstants.maxAmount)}>
                      <DynamicButton id={sendEmailButtonId} additionalClasses={'margin-left-s'} useFlex disabled={shouldButtonBeDisabled} onClick={handleOnSendEmailClick}>
                          <DynamicButton.TextIconItem iconName={EmblaIcons.Email} text={Localizer.createMail_SendMail()}/>
                      </DynamicButton>
                  </Tooltip>
              }
          </div>
        }
        data={data}
        columns={columns}
        isLoading={isLoading}
        fetchFailed={fetchFailed}
        asyncListInput={asyncListInput}
        setAsyncListInput={setAsyncListInput}
        totalRecords={paginationModel?.recordCount ?? 0}    
        filteredRecords={paginationModel?.filteredCount ?? 0}
        defaultExpandedGroups={false}
        renderAdditionalFilterItems={
          <>
            <ApplyFiltrerButton additionalClasses="margin-left-m" onClick={() => setFilterModalOpen(true)} hasFilters={filterApplied}/>
            {filterApplied && <RemoveFiltrerButton additionalClasses="margin-left-m" onClick={clearFilter} />}
            {filterApplied && <KursusOverviewDisplayAppliedFilters filter={asyncListInput.kursusoverviewFilter} containerClasses="margin-left-m"  />}
          </>
        }
        searchPlaceholder={Localizer.kursusoverview_searchPlaceholder()}
        getSubRows={(rowData) => rowData.isParentRow 
          ? !!rowData.laegeKurser && rowData.laegeKurser.length > 0 
            ? rowData.laegeKurser 
            : [KursusoverviewModel.AsPlaceholder(rowData)]
          : []
        }
      />
      {filterModalOpen &&
        <SlideIn
          id={filterModalId}
          title={Localizer.global_filtrer()}
          actionFinishedCallback={closeFilterModal}
          actionOnCloseCallback={closeFilterModal}
          bodyContent={
            <KursusOverviewFilter 
              filter={asyncListInput.kursusoverviewFilter}
              filterCallback={setFilter}
              closeModal={closeFilterModal} 
              permissions={permissions}
            />
          }
          defaultOpen={true}
        />
      }

      {showEmailSlideIn &&

        <SlideIn
            id={modalTarget}
            title={Localizer.sendmail()}
            actionFinishedCallback={closeEmailModal}
            actionOnCloseCallback={closeEmailModal}
            bodyContent={
                <EmailSlideInModalMultipleUsers 
                    users={filteredUserModels}
                    actionFinishedCallback={closeEmailModal}
                />
            }
            defaultOpen={true}
        />
      }
    </>
  );
};